/**
 * Default pin positions and render sizes for part categories within the schematic.
 * Keyed by platform (lowercased keyword match) then by PartCategory string.
 *
 * Position values are fractions 0.0–1.0 of the SCHEMATIC CONTAINER (not the zone rect):
 *   x: 0 = left edge of container, 1 = right edge
 *   y: 0 = top  edge of container, 1 = bottom edge
 *
 * Size values are absolute pixels at the reference container width.
 * They scale visually because the schematic itself scales — these are the
 * dimensions passed to the SVG component and the positioning div.
 *
 * Developer-tunable: changing a value here updates all builds using that
 * category immediately (no migration, no per-build update). Defaults are
 * only applied when the user has not dragged the part.
 */

export interface PartPosition {
  x: number;
  y: number;
}

export interface PartSize {
  w: number; // px
  h: number; // px
}

export interface PartDefault {
  position: PartPosition;
  size: PartSize;
}

// ── AR-15 baked defaults ─────────────────────────────────────────────────────

const AR15_DEFAULTS: Record<string, PartDefault> = {
  Barrel: { position: { x: 0.30, y: 0.45 }, size: { w: 180, h: 36 } },
  'Muzzle Device': { position: { x: 0.08, y: 0.44 }, size: { w: 48, h: 32 } },
  'Handguard / Rail': { position: { x: 0.28, y: 0.46 }, size: { w: 160, h: 32 } },
  'Bolt Carrier Group': { position: { x: 0.52, y: 0.38 }, size: { w: 72, h: 52 } },
  'Charging Handle': { position: { x: 0.55, y: 0.28 }, size: { w: 40, h: 36 } },
  'Upper Receiver': { position: { x: 0.50, y: 0.40 }, size: { w: 100, h: 52 } },
  Optic: { position: { x: 0.48, y: 0.20 }, size: { w: 100, h: 44 } },
  'Light / Laser': { position: { x: 0.32, y: 0.62 }, size: { w: 64, h: 32 } },
  'Trigger Group': { position: { x: 0.62, y: 0.55 }, size: { w: 56, h: 60 } },
  'Lower Receiver': { position: { x: 0.60, y: 0.48 }, size: { w: 96, h: 56 } },
  'Pistol Grip': { position: { x: 0.68, y: 0.62 }, size: { w: 40, h: 72 } },
  'Stock / Brace': { position: { x: 0.80, y: 0.44 }, size: { w: 100, h: 44 } },
  'Sling / Mount': { position: { x: 0.75, y: 0.70 }, size: { w: 32, h: 32 } },
  Other: { position: { x: 0.50, y: 0.50 }, size: { w: 60, h: 40 } }
}

// ── Per-platform overrides ────────────────────────────────────────────────────

// PCC mirrors AR-15 completely.
const PCC_DEFAULTS: Record<string, PartDefault> = { ...AR15_DEFAULTS }

// DMR: longer barrel — pin sits further from muzzle end.
const DMR_DEFAULTS: Record<string, PartDefault> = {
  ...AR15_DEFAULTS,
  Barrel: { position: { x: 0.25, y: 0.45 }, size: AR15_DEFAULTS.Barrel.size }
}

// Bolt-Action: longer barrel + optic pushed rearward.
const BOLT_ACTION_DEFAULTS: Record<string, PartDefault> = {
  ...AR15_DEFAULTS,
  Barrel: { position: { x: 0.25, y: 0.45 }, size: AR15_DEFAULTS.Barrel.size },
  Optic: { position: { x: 0.45, y: 0.18 }, size: AR15_DEFAULTS.Optic.size }
}

// Pistol: short barrel.
const PISTOL_DEFAULTS: Record<string, PartDefault> = {
  ...AR15_DEFAULTS,
  Barrel: { position: { x: 0.35, y: 0.45 }, size: AR15_DEFAULTS.Barrel.size }
}

// Shotgun: wider barrel.
const SHOTGUN_DEFAULTS: Record<string, PartDefault> = {
  ...AR15_DEFAULTS,
  Barrel: { position: { x: 0.28, y: 0.45 }, size: AR15_DEFAULTS.Barrel.size }
}

// ── AK Platform defaults (schematic: 900×320 canvas) ────────────────────────

const AK_DEFAULTS: Record<string, PartDefault> = {
  'Muzzle Device': { position: { x: 0.09, y: 0.48 }, size: { w: 48, h: 32 } },
  Barrel: { position: { x: 0.27, y: 0.48 }, size: { w: 160, h: 22 } },
  'Handguard Upper': { position: { x: 0.24, y: 0.43 }, size: { w: 90, h: 24 } },
  'Handguard Lower': { position: { x: 0.24, y: 0.53 }, size: { w: 90, h: 24 } },
  'Gas Tube / Piston': { position: { x: 0.37, y: 0.41 }, size: { w: 80, h: 20 } },
  'Dust Cover': { position: { x: 0.58, y: 0.36 }, size: { w: 200, h: 28 } },
  'Side Rail Mount': { position: { x: 0.42, y: 0.53 }, size: { w: 36, h: 56 } },
  Optic: { position: { x: 0.55, y: 0.28 }, size: { w: 100, h: 44 } },
  'Trigger Group': { position: { x: 0.55, y: 0.75 }, size: { w: 56, h: 56 } },
  Magazine: { position: { x: 0.55, y: 0.81 }, size: { w: 60, h: 88 } },
  Magazine762: { position: { x: 0.55, y: 0.81 }, size: { w: 60, h: 88 } },
  Magazine545: { position: { x: 0.55, y: 0.81 }, size: { w: 56, h: 88 } },
  MagazineDrum: { position: { x: 0.55, y: 0.81 }, size: { w: 76, h: 88 } },
  'Pistol Grip': { position: { x: 0.66, y: 0.77 }, size: { w: 40, h: 72 } },
  'Stock / Brace': { position: { x: 0.85, y: 0.54 }, size: { w: 96, h: 44 } },
  'Bayonet Lug': { position: { x: 0.18, y: 0.54 }, size: { w: 32, h: 22 } },
  'Sling / Mount': { position: { x: 0.13, y: 0.58 }, size: { w: 32, h: 32 } },
  Other: { position: { x: 0.50, y: 0.50 }, size: { w: 60, h: 40 } }
}

// RPK: longer barrel shifts pin toward centre; sling/mount is larger (bipod combo).
const RPK_DEFAULTS: Record<string, PartDefault> = {
  ...AK_DEFAULTS,
  Barrel: { position: { x: 0.44, y: AK_DEFAULTS.Barrel.position.y }, size: { w: 220, h: 28 } },
  'Sling / Mount': { position: { x: AK_DEFAULTS['Sling / Mount'].position.x, y: 0.70 }, size: { w: 80, h: 100 } }
}

// Generic fallback: centre of container, standard size.
const OTHER_DEFAULTS: Record<string, PartDefault> = Object.fromEntries(
  Object.entries(AR15_DEFAULTS).map(([cat, d]) => [
    cat,
    { position: { x: 0.50, y: 0.50 }, size: d.size }
  ])
)

// ── PART_DEFAULTS map ────────────────────────────────────────────────────────

/** Keyed by lowercase platform keyword → category → default. */
export const PART_DEFAULTS: Record<string, Record<string, PartDefault>> = {
  ar15: AR15_DEFAULTS,
  msr: AR15_DEFAULTS,
  m4: AR15_DEFAULTS,
  m16: AR15_DEFAULTS,
  pcc: PCC_DEFAULTS,
  dmr: DMR_DEFAULTS,
  bolt: BOLT_ACTION_DEFAULTS,
  pistol: PISTOL_DEFAULTS,
  handgun: PISTOL_DEFAULTS,
  shotgun: SHOTGUN_DEFAULTS,
  // AK variants — more-specific keys before 'ak' so p.includes() hits them first
  rpk: RPK_DEFAULTS,
  ak47: AK_DEFAULTS,
  ak74: AK_DEFAULTS,
  akm: AK_DEFAULTS,
  ak: AK_DEFAULTS,
  other: OTHER_DEFAULTS
}

// ── Lookup helper ────────────────────────────────────────────────────────────

const FALLBACK: PartDefault = {
  position: { x: 0.50, y: 0.50 },
  size: { w: 60, h: 40 }
}

/**
 * Returns the default position and render size for a given platform+category pair.
 * Falls back to centre / 60×40 when no specific default is registered.
 */
export function getPartDefaults (
  platform: string | null | undefined,
  category: string | null | undefined
): PartDefault {
  if (!platform || !category) return FALLBACK
  const p = platform.toLowerCase()

  let table: Record<string, PartDefault> | undefined
  for (const key of Object.keys(PART_DEFAULTS)) {
    if (p.includes(key)) { table = PART_DEFAULTS[key]; break }
  }
  if (!table) table = OTHER_DEFAULTS

  return table[category] ?? FALLBACK
}

// ── Rail-mountable categories ────────────────────────────────────────────────

/** Categories whose parts can be assigned a rail position (Top/Right/Bottom/Left). */
export const RAIL_MOUNTABLE_CATEGORIES: string[] = [
  'Light / Laser',
  'Sling / Mount',
  'Optic'
]
